import React from 'react';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import { styled } from '@mui/material/styles';

const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: '#0B0813',
  padding: '40px 20px',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '24px',
  fontWeight: 'bold',
  color: '#00F0FF',
  fontFamily: '"Press Start 2P", cursive',
  marginBottom: '30px',
  textTransform: 'uppercase',
  letterSpacing: '2px',
  textShadow: '0 0 10px rgba(0, 240, 255, 0.5)',
}));

const ContentCard = styled(Card)(({ theme }) => ({
  backgroundColor: 'rgba(11, 8, 19, 0.8)',
  border: '1px solid #00F0FF',
  borderRadius: '5px',
  boxShadow: '0 0 15px rgba(0, 240, 255, 0.1)',
  marginBottom: '20px',
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  color: '#39FF14',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '16px',
  fontWeight: 'bold',
  marginBottom: '10px',
}));

const ContentText = styled(Typography)(({ theme }) => ({
  color: '#FFFFFF',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '14px',
  lineHeight: '1.8',
  marginBottom: '15px',
}));

const WhatIsSteganography: React.FC = () => {
  return (
    <PageContainer>
      <Container maxWidth="lg">
        <SectionTitle>What is Steganography?</SectionTitle>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ContentCard>
              <CardContent>
                <Subtitle>Definition</Subtitle>
                <ContentText>
                  Steganography is the practice of concealing information within other information or objects. Unlike encryption, which hides the content of a message, steganography hides the existence of the message itself.
                </ContentText>
              </CardContent>
            </ContentCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <ContentCard>
              <CardContent>
                <Subtitle>Image Steganography</Subtitle>
                <ContentText>
                  Image steganography hides data inside digital images by modifying the pixel values. The human eye cannot detect these small changes, making the hidden information invisible to casual observers.
                </ContentText>
              </CardContent>
            </ContentCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <ContentCard>
              <CardContent>
                <Subtitle>How It Works</Subtitle>
                <ContentText>
                  The most common method is LSB (Least Significant Bit) encoding. Each pixel in an image is made up of red, green, and blue values. By changing the least significant bit of these values, we can store data without noticeably changing the image appearance.
                </ContentText>
              </CardContent>
            </ContentCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <ContentCard>
              <CardContent>
                <Subtitle>Steganography vs Encryption</Subtitle>
                <ContentText>
                  Encryption scrambles data so it cannot be read without a key. Steganography hides data so it cannot be found. When combined (as in StegSafe), you get dual protection: encrypted data that is also hidden.
                </ContentText>
              </CardContent>
            </ContentCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <ContentCard>
              <CardContent>
                <Subtitle>Common Uses</Subtitle>
                <ContentText>
                  • Protecting sensitive communications<br />
                  • Digital watermarking<br />
                  • Copyright protection<br />
                  • Secure data storage<br />
                  • Privacy protection
                </ContentText>
              </CardContent>
            </ContentCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <ContentCard>
              <CardContent>
                <Subtitle>Security Considerations</Subtitle>
                <ContentText>
                  While steganography can be detected with specialized analysis, combining it with strong encryption makes it extremely secure. StegSafe uses AES-256-GCM encryption before embedding data for maximum security.
                </ContentText>
              </CardContent>
            </ContentCard>
          </Grid>
        </Grid>
      </Container>
    </PageContainer>
  );
};

export default WhatIsSteganography;
